import React, { useState, useEffect, useRef } from "react";
import { Popover, Box, Typography, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getStatusChipColors } from "../utils/claimStatusUtils";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";
import { CLAIMS_MIXPANEL_EVENTS } from "./claimsMixpanelEvents";

/**
 * Status definitions with brief descriptions
 * These represent all possible claim statuses and their meanings
 */
const STATUS_LEGEND = [
  {
    category: "Active - Adjuster Review",
    statuses: [
      { name: "Adjuster Review", description: "Claim is being reviewed by adjusters" },
      { name: "Needs Approval", description: "Claim is pending approval" },
      { name: "Pending Settlement", description: "Settlement is being processed" },
      { name: "Adjuster Action", description: "Adjusters are working on the claim" },
    ],
  },
  {
    category: "Active - Action Required",
    statuses: [
      { name: "Tenant Action Needed", description: "Tenant must complete required actions" },
      { name: "Facility Action Needed", description: "Facility must complete required actions" },
    ],
  },
  {
    category: "Closed",
    statuses: [
      { name: "Closed Settled", description: "Claim has been settled and closed" },
      { name: "Closed", description: "Claim has been closed" },
    ],
  },
  {
    category: "Other",
    statuses: [
      { name: "New Claim", description: "Recently submitted claim" },
      { name: "Incomplete", description: "Claim submission is incomplete" },
    ],
  },
];

/**
 * Elegant popover displaying claim status legend
 * Shows all possible statuses with colored squares and descriptions
 */
export function StatusLegendPopover() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasTrackedOpen = useRef(false);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    // Track status legend opened (only once per session)
    if (!hasTrackedOpen.current) {
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.table.statusLegendOpened, {});
      hasTrackedOpen.current = true;
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        sx={{
          ml: 0.5,
          p: 0.25,
          color: "text.secondary",
          "&:hover": {
            color: "primary.main",
            backgroundColor: "transparent",
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          pointerEvents: "none",
        }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
            sx: {
              pointerEvents: "auto",
              mt: 1,
              p: 2.5,
              maxWidth: 450,
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              borderRadius: 2,
            },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
          Claim Status Legend
        </Typography>

        {STATUS_LEGEND.map((group, groupIndex) => (
          <Box key={groupIndex} sx={{ mb: groupIndex < STATUS_LEGEND.length - 1 ? 2.5 : 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                mb: 1,
                display: "block",
              }}
            >
              {group.category}
            </Typography>

            {group.statuses.map((status, statusIndex) => {
              const colors = getStatusChipColors(status.name);
              return (
                <Box
                  key={statusIndex}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  {/* Colored square indicator */}
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: colors.backgroundColor,
                      borderRadius: 0.5,
                      flexShrink: 0,
                      border: "1px solid",
                      borderColor: "rgba(0, 0, 0, 0.08)",
                    }}
                  />

                  {/* Status name and description */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.primary",
                        lineHeight: 1.3,
                      }}
                    >
                      {status.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.3,
                        display: "block",
                      }}
                    >
                      {status.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Popover>
    </>
  );
}
